import { msg } from "@lingui/core/macro"
import { useLingui } from "@lingui/react"
import { Trans } from "@lingui/react/macro"
import {
  ActionIcon,
  AppShell,
  Avatar,
  Badge,
  Box,
  Burger,
  Drawer,
  Group,
  Menu,
  Text,
  useMantineColorScheme,
} from "@mantine/core"
import { useDisclosure } from "@mantine/hooks"
import { notifications } from "@mantine/notifications"
import { IconArrowsExchange, IconDownload, IconMoon, IconSearch, IconSettings, IconSun } from "@tabler/icons-react"
import { createFileRoute, Link, Navigate, Outlet, useNavigate } from "@tanstack/react-router"
import { useCallback, useEffect, useRef, useState } from "react"
import { AskOverlayProvider, AskTriggerButton } from "../components/AskOverlay"
import { ExperienceModeMenu } from "../components/ExperienceModeMenu"
import { GlobalOfflineBanner } from "../components/GlobalOfflineBanner"
import { LexThemeMenu } from "../components/LexThemeMenu"
import { LocaleSwitcherMenuItems } from "../components/LocaleSwitcher"
import { Logo } from "../components/Logo"
import { ViewingAsBanner } from "../components/ViewingAsBanner"
import { VoxcanonThemeToggle } from "../components/VoxcanonThemeToggle"
import { useAuth } from "../hooks/useAuth"
import { useCompanyQuery } from "../hooks/useCompanyQuery"
import { useNetworkStatus } from "../hooks/useNetworkStatus"
import { withViewTransition } from "../hooks/useViewTransition"
import { fetchSettings, getExperienceMode, getUiLocale } from "../lib/api"
import { authClient, signOut } from "../lib/auth-client"
import { desktopDownloadUrl } from "../lib/desktop-download"
import { clearExperienceModeLocal, setExperienceModeLocal } from "../lib/experience-mode-store"
import { activateLocale, hasUserPickedLocale, isSupportedLocale } from "../lib/i18n"
import { applyLexTheme, readPreviewLexTheme } from "../lib/lex-themes"
import { queryKeys } from "../lib/query-keys"
import { IS_TAURI, openWorkspaceSwitcher } from "../lib/runtime-origin"
import { useProduct, useTenant } from "../lib/tenant"
import { applyTheme, persistThemeCookie, readPersistedTheme } from "../lib/theme-mode"
import { NavLinks } from "./_auth/-layout/NavLinks"
import { NotificationBell } from "./_auth/-layout/NotificationBell"
import classes from "./_auth.module.css"

function ColorSchemeToggle(): React.JSX.Element {
  const { colorScheme, toggleColorScheme } = useMantineColorScheme()
  const { i18n } = useLingui()
  const label = colorScheme === "dark" ? i18n._(msg`Switch to light mode`) : i18n._(msg`Switch to dark mode`)

  return (
    <ActionIcon
      variant="subtle"
      onClick={() => withViewTransition(toggleColorScheme)}
      title={label}
      aria-label={label}
      size="lg"
    >
      {colorScheme === "dark" ? <IconSun size={18} /> : <IconMoon size={18} />}
    </ActionIcon>
  )
}

function AuthLayout(): React.JSX.Element | null {
  const { user, loading } = useAuth()
  const tenantState = useTenant()
  const product = useProduct()
  const navigate = useNavigate()
  const { i18n } = useLingui()
  const { data: activeOrg } = authClient.useActiveOrganization()
  const { data: orgs } = authClient.useListOrganizations()
  const { data: activeMember } = authClient.useActiveMember()
  const autoActivated = useRef(false)
  const { isOnline } = useNetworkStatus()
  // Pulled up to support the mobile-only theme-toggle Menu.Item below
  // (Lexicanon — Voxcanon has its own VoxcanonThemeToggle in the menu).
  const { colorScheme, toggleColorScheme } = useMantineColorScheme()

  // Phase 5 — apply per-company `default_ui_locale` for users who haven't
  // picked a locale on this device yet. Boot no longer writes the browser
  // fallback to localStorage, so this can actually run on first visit.
  const settingsQuery = useCompanyQuery({
    key: queryKeys.companyUiLocale,
    queryFn: (client) => fetchSettings(client),
    enabled: !!user,
    staleTime: 5 * 60_000,
  })
  const localeApplied = useRef(false)
  useEffect(() => {
    if (localeApplied.current) return
    if (!user) return
    if (user.uiLocale && isSupportedLocale(user.uiLocale)) {
      localeApplied.current = true
      void activateLocale(user.uiLocale, { persist: true })
      return
    }
    if (hasUserPickedLocale()) {
      localeApplied.current = true
      return
    }
    if (!settingsQuery.data) return
    const companyDefault = (settingsQuery.data as Record<string, string>).default_ui_locale
    if (companyDefault && isSupportedLocale(companyDefault)) {
      localeApplied.current = true
      void activateLocale(companyDefault, { persist: true })
    }
  }, [settingsQuery.data, user])

  // Experience mode + UI locale live on the user row. get-session is cookie
  // cached, so hydrate from the dedicated endpoints (raw SQL, not cached).
  const prefsHydratedFor = useRef<string | null>(null)
  useEffect(() => {
    if (!user) return
    if (prefsHydratedFor.current === user.id) return
    prefsHydratedFor.current = user.id
    void getExperienceMode()
      .then(setExperienceModeLocal)
      .catch(() => undefined)
    void getUiLocale()
      .then((locale) => {
        if (!locale) return
        void activateLocale(locale, { persist: true })
      })
      .catch(() => undefined)
  }, [user])

  useEffect(() => {
    if (autoActivated.current) return
    if (user?.isAdmin) return
    if (user?.activeOrganizationId) return
    if (!orgs || orgs.length !== 1) return

    autoActivated.current = true
    authClient.organization
      .setActive({ organizationId: orgs[0].id })
      .then(() => window.location.reload())
      .catch(() => {
        autoActivated.current = false
      })
  }, [user, orgs])

  // Lexicanon — once auth resolves, the user's saved theme override takes
  // priority over the company default / cached pre-paint pick, UNLESS a
  // transient `?theme=` preview is active for this tab. Caching it (the
  // default in applyLexTheme) makes it win on the next pre-paint too.
  const lexThemeSyncedFor = useRef<string | null>(null)
  useEffect(() => {
    if (product !== "lexicanon") return
    if (!user) return
    if (lexThemeSyncedFor.current === user.id) return
    lexThemeSyncedFor.current = user.id
    const stored = user.lexThemePreference
    if (!stored) return
    if (readPreviewLexTheme()) return
    applyLexTheme(stored)
  }, [product, user])

  // Voxcanon dual-theme — once auth resolves, the user's stored
  // preference takes priority over whatever main.tsx picked from the
  // cookie/system pref. We mirror it back to the cookie so the next
  // first-paint matches without a flash.
  const themeSyncedFor = useRef<string | null>(null)
  useEffect(() => {
    if (product !== "voxcanon") return
    if (!user) return
    if (themeSyncedFor.current === user.id) return
    themeSyncedFor.current = user.id
    const stored = user.themePreference
    if (!stored) return
    if (readPersistedTheme() === stored) return
    applyTheme(stored)
    persistThemeCookie(stored)
  }, [product, user])

  const [mobileNavOpened, mobileNav] = useDisclosure(false)
  const [exitingCompany, setExitingCompany] = useState(false)
  const exitCompanyView = useCallback((): void => {
    setExitingCompany(true)
    void authClient.organization
      .setActive({ organizationId: null })
      .then(() => {
        window.location.href = "/"
      })
      .catch(() => {
        setExitingCompany(false)
        notifications.show({
          title: i18n._(msg`Could not exit company view`),
          message: i18n._(msg`Please try again.`),
          color: "danger",
        })
      })
  }, [i18n])

  if (loading) return null
  if (!user) return <Navigate to="/login" />
  const isAdmin = tenantState.isAdmin && user.isAdmin && !user.activeOrganizationId
  const isViewingCompany = tenantState.isAdmin && user.isAdmin && !!user.activeOrganizationId
  const companyName = tenantState.branding?.name || activeOrg?.name || null
  const viewingName = companyName && companyName.trim() !== "" ? companyName : i18n._(msg`this company`)
  const navHeight = product === "voxcanon" ? 64 : 56
  const headerHeight = isViewingCompany ? `calc(${navHeight + 64}px + env(safe-area-inset-top))` : navHeight
  // Audit log is visible to global super-admins and to owners/admins of the
  // active org (the backend enforces the same rule). `user.isAdmin` is the global
  // role; `activeMember.role` is the per-org role from Better Auth.
  const orgRole = (activeMember as { role?: string } | null | undefined)?.role
  // Super-admin or owner/admin of the active org. Same bar the backend uses for
  // the audit log AND for the admin-notification routes, so the bell only polls
  // for users whose requests won't 403.
  const isOrgManager = user.isAdmin || orgRole === "owner" || orgRole === "admin"
  const canViewAudit = isOrgManager
  // Ask-anywhere overlay (⌘K + ✦) is available wherever the Ask AI page is —
  // any org member, or a super-admin viewing a company / with no active org.
  // Lite mode hides Ask (and the matching nav items) so the chrome matches
  // "record, speakers, transcript, export — nothing else."
  const isLite = user.experienceMode === "lite"
  const askEnabled = (isAdmin || !!user.activeOrganizationId) && !isLite

  return (
    <AskOverlayProvider enabled={askEnabled}>
      <AppShell header={{ height: headerHeight }} padding="md">
        <AppShell.Header>
          <div className={classes.shellHeader}>
            {isViewingCompany ? (
              <ViewingAsBanner companyName={viewingName} onExit={exitCompanyView} exiting={exitingCompany} />
            ) : null}
            <Group className={classes.headerRow} px="md" justify="space-between">
              <Group gap={product === "voxcanon" ? "xl" : "md"}>
                <Burger
                  className={classes.burger}
                  opened={mobileNavOpened}
                  onClick={mobileNav.toggle}
                  hiddenFrom="navFull"
                  size="sm"
                  aria-label={i18n._(msg`Toggle navigation`)}
                />
                <Link
                  to="/"
                  style={{
                    textDecoration: "none",
                    color: "inherit",
                    display: "flex",
                    alignItems: "center",
                  }}
                >
                  <Logo showCompany={companyName} iconless={product === "voxcanon"} />
                </Link>
                <Box visibleFrom="navFull">
                  <NavLinks
                    isAdmin={isAdmin}
                    hasOrg={!!user.activeOrganizationId}
                    canViewAudit={canViewAudit}
                    activeOrgId={user.activeOrganizationId}
                    isLite={isLite}
                  />
                </Box>
              </Group>
              <Group gap="xs" wrap="nowrap">
                {askEnabled && <AskTriggerButton />}
                {product === "voxcanon" && (
                  <ActionIcon
                    variant="subtle"
                    size="lg"
                    aria-label={i18n._(msg`Search`)}
                    title={i18n._(msg`Search`)}
                    onClick={() => navigate({ to: "/", search: { focusSearch: true } })}
                  >
                    <IconSearch size={18} />
                  </ActionIcon>
                )}
                <NotificationBell canView={isOrgManager} />
                {/* Settings + theme toggle stay desktop-only — on phones they're
                reachable via the burger nav drawer (Settings link) and the
                avatar menu (theme toggle moved into Menu.Item below). Pulling
                them out of the row stops the header from wrapping to a
                second line on iPhone 13 Mini. */}
                {(isAdmin || user.activeOrganizationId) && (
                  <Box visibleFrom="sm">
                    <ActionIcon
                      component={Link}
                      to="/settings"
                      variant="subtle"
                      size="lg"
                      aria-label={i18n._(msg`Settings`)}
                      title={i18n._(msg`Settings`)}
                    >
                      <IconSettings size={18} />
                    </ActionIcon>
                  </Box>
                )}
                {product === "lexicanon" && (
                  <Box visibleFrom="sm">
                    <ColorSchemeToggle />
                  </Box>
                )}
                <Menu shadow="md" width={200} position="bottom-end">
                  <Menu.Target>
                    <ActionIcon variant="subtle" size="lg" radius="xl" aria-label={i18n._(msg`User menu`)}>
                      <Avatar size="sm" color="brand" radius="xl">
                        {user.name?.charAt(0)?.toUpperCase() || "?"}
                      </Avatar>
                    </ActionIcon>
                  </Menu.Target>
                  <Menu.Dropdown>
                    <Menu.Label>
                      <Group gap="xs" justify="space-between" wrap="nowrap">
                        <Text size="sm" fw={500}>
                          {user.name}
                        </Text>
                        {user.isAdmin && (
                          <Badge size="xs" variant="light" color="brand" radius="sm">
                            <Trans>Admin</Trans>
                          </Badge>
                        )}
                      </Group>
                    </Menu.Label>
                    <Menu.Label c="dimmed">{user.email}</Menu.Label>
                    {isViewingCompany && (
                      <>
                        <Menu.Divider />
                        <Menu.Item
                          onClick={() => {
                            // Same handler as the viewing-as banner. Await the
                            // active-org clear before navigating — a synchronous
                            // reload raced the request and left the org active.
                            exitCompanyView()
                          }}
                        >
                          <Trans>Exit company view</Trans>
                        </Menu.Item>
                      </>
                    )}
                    <Menu.Divider />
                    {product === "voxcanon" && (
                      <>
                        <Menu.Item closeMenuOnClick={false} component="div">
                          <VoxcanonThemeToggle initialTheme={user.themePreference ?? undefined} />
                        </Menu.Item>
                        <Menu.Divider />
                      </>
                    )}
                    {product === "lexicanon" && (
                      <>
                        <Box hiddenFrom="sm">
                          <Menu.Item
                            closeMenuOnClick={false}
                            leftSection={colorScheme === "dark" ? <IconSun size={14} /> : <IconMoon size={14} />}
                            onClick={() => withViewTransition(toggleColorScheme)}
                          >
                            {colorScheme === "dark" ? (
                              <Trans>Switch to light mode</Trans>
                            ) : (
                              <Trans>Switch to dark mode</Trans>
                            )}
                          </Menu.Item>
                          <Menu.Divider />
                        </Box>
                        <LexThemeMenu current={user.lexThemePreference} />
                        <ExperienceModeMenu current={user.experienceMode} />
                      </>
                    )}
                    <LocaleSwitcherMenuItems />
                    <Menu.Divider />
                    {!IS_TAURI && desktopDownloadUrl() ? (
                      <Menu.Item
                        component="a"
                        href={desktopDownloadUrl() ?? undefined}
                        leftSection={<IconDownload size={14} />}
                      >
                        <Trans>Download desktop app</Trans>
                      </Menu.Item>
                    ) : null}
                    {IS_TAURI && (
                      <Menu.Item leftSection={<IconArrowsExchange size={14} />} onClick={openWorkspaceSwitcher}>
                        <Trans>Switch workspace</Trans>
                      </Menu.Item>
                    )}
                    <Menu.Item
                      onClick={() => {
                        clearExperienceModeLocal()
                        void signOut().then(() => (window.location.href = "/login"))
                      }}
                    >
                      <Trans>Sign out</Trans>
                    </Menu.Item>
                    <Menu.Divider />
                    <Menu.Item
                      closeMenuOnClick={false}
                      onClick={() => {
                        const sha = (import.meta.env.VITE_COMMIT_SHA ?? "").slice(0, 7)
                        if (!sha) return
                        void navigator.clipboard?.writeText(sha)
                        notifications.show({
                          title: i18n._(msg`Build info copied`),
                          message: sha,
                          color: "info",
                          autoClose: 2_000,
                        })
                      }}
                      style={{ fontFamily: "var(--mantine-font-family-monospace)", fontSize: "0.7rem" }}
                      c="dimmed"
                    >
                      <Trans>Copy build info</Trans>
                    </Menu.Item>
                  </Menu.Dropdown>
                </Menu>
              </Group>
            </Group>
          </div>
        </AppShell.Header>

        <Drawer
          opened={mobileNavOpened}
          onClose={mobileNav.close}
          title={<Logo showCompany={companyName} iconless={product === "voxcanon"} />}
          position="left"
          size="xs"
          hiddenFrom="navFull"
          // Sit above the app header — the Voxcanon header is pinned at
          // z-index 202 (above Mantine's default 200 Drawer layer), so without
          // this the open drawer renders *under* the header on that brand.
          zIndex={300}
          classNames={{
            header: "lex-nav-drawer-header",
            body: "lex-nav-drawer-body",
          }}
        >
          <nav className="lex-nav-drawer-scroll" aria-label={i18n._(msg`Main navigation`)}>
            <NavLinks
              isAdmin={isAdmin}
              hasOrg={!!user.activeOrganizationId}
              canViewAudit={canViewAudit}
              activeOrgId={user.activeOrganizationId}
              isLite={isLite}
              onNavigate={mobileNav.close}
              layout="vertical"
            />
          </nav>
          <div className="lex-nav-drawer-footer">
            <div className="lex-nav-drawer-user">
              <Avatar size="md" color="brand" radius="xl">
                {user.name?.charAt(0)?.toUpperCase() || "?"}
              </Avatar>
              <div className="lex-nav-drawer-user-text">
                <Text size="sm" fw={600} truncate>
                  {user.name}
                </Text>
                <Text size="xs" c="dimmed" truncate>
                  {user.email}
                </Text>
              </div>
              {product === "lexicanon" && <ColorSchemeToggle />}
            </div>
            {product === "voxcanon" && (
              <div className="lex-nav-drawer-toggle">
                <VoxcanonThemeToggle initialTheme={user.themePreference ?? undefined} />
              </div>
            )}
          </div>
        </Drawer>

        <AppShell.Main>
          {!isOnline && <GlobalOfflineBanner />}
          <Outlet />
        </AppShell.Main>
      </AppShell>
    </AskOverlayProvider>
  )
}

export const Route = createFileRoute("/_auth")({
  component: AuthLayout,
})
